import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const sftTrainingComponent: ComponentDef = {
  id: 'sft-training',
  name: 'SFT Training',
  description: 'Supervised fine-tuning on instruction-response pairs.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'vocab_size', label: 'Vocab size', type: 'number', default: 32000, min: 1000, max: 200000, step: 1000 },
    {
      name: 'variant',
      label: 'Data Format',
      type: 'select',
      default: 'chatml',
      options: [
        { label: 'ChatML', value: 'chatml' },
        { label: 'Alpaca', value: 'alpaca' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const vocab_size = Number(params.vocab_size);
    const variant = String(params.variant || 'chatml');

    const g = new GraphBuilder();

    if (variant === 'chatml') {
      // ChatML format: <|im_start|>system\n...<|im_end|>\n<|im_start|>user\n...<|im_end|>\n<|im_start|>assistant\n...
      g.input('system_prompt', [seq_len]);
      g.input('user_message', [seq_len]);
      g.input('assistant_response', [seq_len]);

      g.embedding('embed_system', 'system_prompt', [vocab_size, d_model]);
      g.embedding('embed_user', 'user_message', [vocab_size, d_model]);
      g.embedding('embed_assistant', 'assistant_response', [vocab_size, d_model]);

      g.concat('context', ['embed_system', 'embed_user'], [2 * seq_len, d_model]);
      g.concat('full_seq', ['context', 'embed_assistant'], [3 * seq_len, d_model]);

      g.linear('transformer', 'full_seq', [d_model, d_model]);
      g.linear('logits', 'transformer', [d_model, vocab_size]);

      // Mask loss to only assistant tokens (ignore system + user turns)
      g.mask('mask_assistant', 'logits');
      g.loss('loss', 'mask_assistant', 'assistant_response');
      g.scale('sft', 'loss', 'SFT Step (ChatML)');
      g.output('out', 'sft');

    } else {
      // Alpaca format: ### Instruction:\n...\n### Input:\n...\n### Response:\n...
      g.input('instruction', [seq_len]);
      g.input('input_ctx', [seq_len]);
      g.input('response', [seq_len]);

      g.embedding('embed_instr', 'instruction', [vocab_size, d_model]);
      g.embedding('embed_input', 'input_ctx', [vocab_size, d_model]);
      g.embedding('embed_response', 'response', [vocab_size, d_model]);

      g.concat('prompt', ['embed_instr', 'embed_input'], [2 * seq_len, d_model]);
      g.concat('full_seq', ['prompt', 'embed_response'], [3 * seq_len, d_model]);

      g.linear('transformer', 'full_seq', [d_model, d_model]);
      g.linear('logits', 'transformer', [d_model, vocab_size]);

      // Mask loss to only response tokens (ignore instruction + input)
      g.mask('mask_response', 'logits');
      g.loss('loss', 'mask_response', 'response');
      g.scale('sft', 'loss', 'SFT Step (Alpaca)');
      g.output('out', 'sft');
    }

    return g.build();
  }
};
